import { useRouter } from 'next/router';
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import StickyButton from '@/components/atom/buttons/StickyButton';
import TextareaMobile from '@/components/atom/form/TextareaMobile';
import AppContainer from '@/components/molecules/app-container';
import AppHeader from '@/components/molecules/app-header';
import {
  useSetReportBoardCommentMutation,
  useSetReportBoardPostMutation,
} from '@/hooks/community/mobile/community.gql.impl';
import { useSetReportAnswerMutation, useSetReportQuestionMutation } from '@/hooks/qna/mobile/qna.gql.impl';
import useToast from '@/hooks/useToast';
import { historyBack } from '@/utils/mobile/RNPlugins';
import { RadioGroup, RadioItem } from 'doctornow-design-system';

export const REPORT_TYPE = {
  QUESTION: 'question',
  ANSWER: 'answer',
  BOARD_POST: 'board_post',
  BOARD_COMMENT: 'board_comment',
} as const;

export type REPORT_TYPE = (typeof REPORT_TYPE)[keyof typeof REPORT_TYPE];

export const REPORT_KEY = {
  SERVICES: 'Service',
  PRIVACY: 'Privacy',
  MARKETING: 'Marketing',
  ABUSE: 'Abuse',
  ETC: 'Etc',
  LEWDNESS: 'Lewdness',
  PROFILE: 'Profile',
  DIGRESSION: 'Digression',
} as const;

export type REPORT_KEY = (typeof REPORT_KEY)[keyof typeof REPORT_KEY];

export interface ReportModel {
  id: REPORT_KEY;
  text: string;
}

const ReportMenu: ReportModel[] = [
  { id: REPORT_KEY.SERVICES, text: '서비스와 관련없는 내용' },
  { id: REPORT_KEY.PRIVACY, text: '개인정보 유출 위험' },
  { id: REPORT_KEY.MARKETING, text: '광고 또는 홍보 글' },
  { id: REPORT_KEY.ABUSE, text: '음란성, 욕설 등 부적절한 내용' },
  { id: REPORT_KEY.ETC, text: '기타' },
];

const ReportTemplate = () => {
  const router = useRouter();
  const targetId = router.query.targetId as string;
  const reportContentType = router.query.type as string;
  const reportType = router.query.report_type as string;
  const { showToast } = useToast();

  const CommunityReportMenu: ReportModel[] = [
    { id: REPORT_KEY.ABUSE, text: '욕설, 폭언, 혐오, 발언' },
    { id: REPORT_KEY.LEWDNESS, text: '음란성, 선정성 글' },
    { id: REPORT_KEY.PRIVACY, text: '개인정보 노출' },
    { id: REPORT_KEY.MARKETING, text: '스팸, 광고성 글' },
    {
      id: reportType === 'user' ? REPORT_KEY.PROFILE : REPORT_KEY.DIGRESSION,
      text: reportType === 'user' ? '부적절한 닉네임과 프로필 사진' : '주제에서 벗어난 글',
    },
  ];
  const menus =
    reportContentType === REPORT_TYPE.BOARD_POST || reportContentType === REPORT_TYPE.BOARD_COMMENT
      ? CommunityReportMenu
      : ReportMenu;
  const message: string = useMemo(() => {
    if (reportType === 'user') {
      if (reportContentType === REPORT_TYPE.ANSWER) {
        return '의료진을 신고했어요.';
      }
      return '사용자를 신고했어요.';
    }

    if (reportType === REPORT_TYPE.QUESTION || reportContentType === REPORT_TYPE.BOARD_POST) {
      return '게시글을 신고했어요.';
    }

    if (reportContentType === REPORT_TYPE.BOARD_COMMENT) {
      return '댓글을 신고했어요.';
    }

    return '답변을 신고했어요.';
  }, [reportType, reportContentType]);

  const [reason, setReason] = useState<ReportModel>({} as ReportModel);
  const [detailReason, setDetailReason] = useState<string>('');

  const onSuccess = useCallback(() => {
    showToast({ message });
  }, [message, showToast]);

  const onError = useCallback(() => {
    showToast({
      message: '일시적 오류가 발생했습니다. 잠시후 다시 시도해주세요.',
    });
  }, [showToast]);

  const { mutate: questionReportMutate } = useSetReportQuestionMutation({
    onSuccess,
    onError,
  });
  const { mutate: answerReportMutate } = useSetReportAnswerMutation({
    onSuccess,
    onError,
  });
  const { mutate: setReportBoardPostMutate } = useSetReportBoardPostMutation({
    onSuccess,
    onError,
  });
  const { mutate: setReportBoardCommentMutate } = useSetReportBoardCommentMutation({
    onSuccess,
    onError,
  });
  const onChangeReason = (reason: ReportModel) => {
    setReason(reason);
  };

  const onChangeTextarea = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setDetailReason(event.target.value.replace(/^\s+/g, ''));
  };

  const checkDisabled = () => {
    if (reason.id === REPORT_KEY.ETC) {
      return detailReason.length === 0;
    }

    return !reason.text;
  };

  const onSubmitReport = useCallback(async () => {
    const submitReason = reason.id === REPORT_KEY.ETC ? detailReason : reason.text;

    switch (reportContentType) {
      case REPORT_TYPE.QUESTION: {
        questionReportMutate({
          request: { questionPid: targetId, reason: submitReason },
        });
        break;
      }
      case REPORT_TYPE.ANSWER: {
        answerReportMutate({
          request: { answerPid: targetId, reason: submitReason },
        });
        break;
      }
      case REPORT_TYPE.BOARD_POST: {
        setReportBoardPostMutate({
          request: { boardPostPid: targetId, reason: submitReason },
        });
        break;
      }
      case REPORT_TYPE.BOARD_COMMENT: {
        setReportBoardCommentMutate({
          request: { boardCommentPid: targetId, reason: submitReason },
        });
        break;
      }
    }

    setTimeout(() => historyBack(), 2000);
  }, [
    answerReportMutate,
    detailReason,
    targetId,
    questionReportMutate,
    reason.id,
    reason.text,
    reportContentType,
    setReportBoardPostMutate,
    setReportBoardCommentMutate,
  ]);

  return (
    <AppContainer>
      <AppHeader title='신고하기' onClickBackButton={historyBack} />
      <Wrapper>
        <Title>이유를 알려주세요</Title>
        <RadioGroup
          value={reason.id}
          onChange={(value) => {
            const findReason = menus.find((item) => item.id === value);

            if (findReason) {
              onChangeReason(findReason);
            }
          }}
          className='flex flex-col gap-3'
        >
          {menus.map((item) => (
            <RadioItem key={item.id} value={item.id}>
              {item.text}
            </RadioItem>
          ))}
        </RadioGroup>
        <QuickAnswerReportTextarea
          display={reason.id === REPORT_KEY.ETC}
          id='quickAnswerQuestionReport'
          labelSpacing={'8px'}
          height={'144px'}
          label={' '}
          placeholder='내용을 작성해주세요.'
          maxLength={500}
          value={detailReason}
          showCharacterCount={true}
          onChange={onChangeTextarea}
        />
      </Wrapper>

      <StickyButton>
        <StickyButton.Button disabled={checkDisabled()} type='button' onClick={onSubmitReport} text={'의견 전달하기'} />
      </StickyButton>
    </AppContainer>
  );
};

export default ReportTemplate;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px;
`;

const Title = styled.section`
  padding: 16px 0 8px;

  ${({ theme }) => `
    ${theme.typography.UI20SB};
    color: ${theme.color.gray700};
  `};
`;

export const QuickAnswerReportTextarea = styled(TextareaMobile)<{
  display: boolean;
}>`
  visibility: ${({ display }) => (display ? 'visible' : 'hidden')};
  opacity: ${({ display }) => (display ? 1 : 0)};
  transition: all 0.2s ease-in;
`;
